import { createSlice } from '@reduxjs/toolkit';
import { ILocation, ILocationContent } from 'interfaces';
import {
  getCountryList,
  getLocations,
  getLocationsByCountry,
} from 'services/locations';
import { setUserLocation } from 'services/users';
import { localStorageManager } from 'services/localStorage';
import { showErrorAlert } from 'redux/utils';
import { ILocationState } from './locationTypes';
import { AppThunkAction } from '../../store';

export const initialState: ILocationState = {
  locations: undefined,
  currentLocation: localStorageManager.getCurrentLocation() ?? undefined,
  locationsByCountry: undefined,
  isFetchingLocation: false,
  countryList: undefined,
};

export const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    fetchLocation: (state, action) => {
      return {
        ...state,
        locations: action.payload,
      };
    },
    setLocationByCountry: (state, action) => {
      return {
        ...state,
        locationsByCountry: action.payload,
      };
    },
    setCurrentLocation: (state, action) => {
      return {
        ...state,
        currentLocation: action.payload,
      };
    },
    isFetchingLocation: (state, action) => {
      return {
        ...state,
        isFetchingLocation: action.payload,
      };
    },
    setCountryList: (state, action) => {
      return {
        ...state,
        countryList: action.payload,
      };
    },
  },
});

export const {
  fetchLocation,
  setLocationByCountry,
  setCurrentLocation,
  isFetchingLocation,
  setCountryList,
} = locationSlice.actions;

export default locationSlice.reducer;

export const fetchAndSetLocations = (): AppThunkAction => async (dispatch) => {
  try {
    const location = await getLocations();
    dispatch(fetchLocation(location as ILocationContent));
  } catch (e) {
    if (e instanceof Error) {
      throw e;
    }
  }
};

export const getCitiesOfCountry =
  (country: string): AppThunkAction =>
  async (dispatch) => {
    try {
      dispatch(isFetchingLocation(true));
      const locations = await getLocationsByCountry(country);
      dispatch(setLocationByCountry(locations));
      dispatch(isFetchingLocation(false));
    } catch (e) {
      showErrorAlert(dispatch, e, 'Could not get the city list');
    }
  };

export const patchCurrentLocation =
  (newLocation: ILocation): AppThunkAction =>
  async (dispatch) => {
    const user = await setUserLocation({ lastVisitedLocation: newLocation });
    user?.lastVisitedLocation &&
      dispatch(setCurrentLocation(user.lastVisitedLocation));
  };

export const fetchAndSetCountryList =
  (): AppThunkAction => async (dispatch) => {
    try {
      const payload = await getCountryList();
      dispatch(setCountryList(payload));
    } catch (err) {
      showErrorAlert(dispatch, err, 'Could not get the country list');
    }
  };
